<template>
  <div class="page-container">
    <Sidebar />
    <div class="main-content">
      <div class="page-header">
        <h1>2025版药典管理</h1>
        <div class="header-actions">
          <button class="add-btn" @click="showAddModal">+ 新增药材</button>
          <button class="refresh-btn" @click="refreshData">刷新</button>
        </div>
      </div>

      <div class="filter-bar">
        <input v-model="searchQuery" type="text" class="search-input" placeholder="搜索药材名称..." @keyup.enter="handleSearch" />
        <select v-model="activeFilter" class="status-select" @change="handleSearch">
          <option value="">全部状态</option>
          <option value="true">已启用</option>
          <option value="false">已禁用</option>
        </select>
        <button class="search-btn" @click="handleSearch">搜索</button>
        <button class="reset-btn" @click="resetFilters">重置</button>
      </div>

      <div class="content-body">
        <div class="loading" v-if="loading">加载中...</div>
        <div class="error" v-else-if="error">{{ error }}</div>

        <table class="data-table" v-else>
          <thead>
            <tr>
              <th>ID</th>
              <th>药材名</th>
              <th>状态</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in pharmacopoeiaList" :key="item.id">
              <td>{{ item.id }}</td>
              <td class="name-cell">{{ item.name }}</td>
              <td>
                <span :class="['status-tag', item.is_active ? 'active' : 'inactive']">
                  {{ item.is_active ? '启用' : '禁用' }}
                </span>
              </td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td class="action-cell">
                <button class="edit-btn" @click="editItem(item)">编辑</button>
                <button class="delete-btn" @click="deleteItem(item)">删除</button>
              </td>
            </tr>
            <tr v-if="pharmacopoeiaList.length === 0">
              <td colspan="5" class="empty">暂无药典数据</td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button :disabled="currentPage === 1" @click="changePage(1)">首页</button>
          <button :disabled="currentPage === 1" @click="changePage(currentPage - 1)">上一页</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">下一页</button>
          <button :disabled="currentPage === totalPages" @click="changePage(totalPages)">末页</button>
          <select v-model="pageSize" @change="changePageSize" class="page-size-select">
            <option :value="10">10条/页</option>
            <option :value="20">20条/页</option>
            <option :value="50">50条/页</option>
            <option :value="100">100条/页</option>
          </select>
        </div>

        <div class="total" v-if="!loading && !error">共 {{ total }} 条记录</div>
      </div>

      <!-- 编辑/新增弹窗 -->
      <div class="modal-overlay" v-if="showEditModal" @click.self="closeEditModal">
        <div class="modal-content edit-modal">
          <div class="modal-header">
            <h2>{{ editMode === 'add' ? '新增药材' : '编辑药材' }}</h2>
            <button class="close-btn" @click="closeEditModal">×</button>
          </div>
          <div class="modal-body edit-body">
            <div class="form-row">
              <label>药材名 <span class="required">*</span></label>
              <input v-model="editForm.name" type="text" class="form-input" placeholder="如：山药" />
            </div>
            <div class="form-row">
              <label>完整标准内容</label>
              <textarea v-model="editForm.content" class="form-textarea large" placeholder="药典标准内容，包括来源、性状、鉴别、检查、性味归经、功能主治、用法用量、贮藏等"></textarea>
            </div>
            <div class="form-row">
              <label>状态</label>
              <select v-model="editForm.is_active" class="form-select">
                <option :value="true">启用</option>
                <option :value="false">禁用</option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button class="cancel-btn" @click="closeEditModal">取消</button>
            <button class="save-btn" @click="saveItem" :disabled="saving">{{ saving ? '保存中...' : '保存' }}</button>
          </div>
        </div>
      </div>

      <!-- 删除确认弹窗 -->
      <div class="modal-overlay" v-if="showDeleteModal" @click.self="closeDeleteModal">
        <div class="modal-content delete-modal">
          <div class="modal-header">
            <h2>确认删除</h2>
            <button class="close-btn" @click="closeDeleteModal">×</button>
          </div>
          <div class="modal-body">
            <p>确定要删除药材 <strong>{{ deleteTarget?.name }}</strong> 吗？此操作不可恢复。</p>
          </div>
          <div class="modal-footer">
            <button class="cancel-btn" @click="closeDeleteModal">取消</button>
            <button class="confirm-delete-btn" @click="confirmDelete" :disabled="deleting">{{ deleting ? '删除中...' : '确认删除' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import { formatDate } from '../utils/dateFormat'
import { getAdminPassword } from '../utils/api'
import axios from 'axios'

export default {
  name: 'Pharmacopoeia2025List',
  components: { Sidebar },
  data() {
    return {
      pharmacopoeiaList: [],
      loading: true,
      error: '',
      searchQuery: '',
      activeFilter: '',
      currentPage: 1,
      pageSize: 20,
      total: 0,
      showEditModal: false,
      editMode: 'add',
      editForm: this.getEmptyForm(),
      editTarget: null,
      saving: false,
      showDeleteModal: false,
      deleteTarget: null,
      deleting: false
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  mounted() {
    this.fetchPharmacopoeia()
  },
  methods: {
    getEmptyForm() {
      return {
        name: '',
        content: '',
        is_active: true
      }
    },
    async fetchPharmacopoeia() {
      this.loading = true
      this.error = ''
      try {
        const params = { page: this.currentPage, pageSize: this.pageSize }
        if (this.searchQuery) params.search = this.searchQuery
        if (this.activeFilter) params.is_active = this.activeFilter
        const response = await axios.get('/api/admin/pharmacopoeia-2025', {
          params,
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          const data = response.data.data
          this.pharmacopoeiaList = data?.list || []
          this.total = data?.total || 0
        } else {
          this.error = response.data?.msg || '获取数据失败'
        }
      } catch (err) {
        this.error = '获取数据失败，请稍后重试'
        console.error('Error fetching pharmacopoeia:', err)
      } finally {
        this.loading = false
      }
    },
    showAddModal() {
      this.editMode = 'add'
      this.editForm = this.getEmptyForm()
      this.editTarget = null
      this.showEditModal = true
    },
    editItem(item) {
      this.editMode = 'edit'
      this.editTarget = item
      this.editForm = { ...this.getEmptyForm(), ...item }
      this.showEditModal = true
    },
    closeEditModal() {
      this.showEditModal = false
      this.editForm = this.getEmptyForm()
      this.editTarget = null
    },
    async saveItem() {
      if (!this.editForm.name.trim()) {
        alert('请输入药材名')
        return
      }
      this.saving = true
      try {
        const url = this.editMode === 'add'
          ? '/api/admin/pharmacopoeia-2025'
          : `/api/admin/pharmacopoeia-2025/${this.editTarget.id}`
        const method = this.editMode === 'add' ? 'post' : 'put'
        const response = await axios[method](url, this.editForm, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          this.closeEditModal()
          this.fetchPharmacopoeia()
          alert(this.editMode === 'add' ? '新增成功' : '编辑成功')
        } else {
          alert(response.data?.msg || '保存失败')
        }
      } catch (err) {
        alert('保存失败，请稍后重试')
        console.error('Error saving item:', err)
      } finally {
        this.saving = false
      }
    },
    deleteItem(item) {
      this.deleteTarget = item
      this.showDeleteModal = true
    },
    closeDeleteModal() {
      this.showDeleteModal = false
      this.deleteTarget = null
    },
    async confirmDelete() {
      this.deleting = true
      try {
        const response = await axios.delete(`/api/admin/pharmacopoeia-2025/${this.deleteTarget.id}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data?.code === 200) {
          this.closeDeleteModal()
          this.fetchPharmacopoeia()
          alert('删除成功')
        } else {
          alert(response.data?.msg || '删除失败')
        }
      } catch (err) {
        alert('删除失败，请稍后重试')
        console.error('Error deleting item:', err)
      } finally {
        this.deleting = false
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchPharmacopoeia()
    },
    resetFilters() {
      this.searchQuery = ''
      this.activeFilter = ''
      this.currentPage = 1
      this.fetchPharmacopoeia()
    },
    changePage(page) {
      this.currentPage = page
      this.fetchPharmacopoeia()
    },
    changePageSize() {
      this.currentPage = 1
      this.fetchPharmacopoeia()
    },
    refreshData() {
      this.fetchPharmacopoeia()
    },
    formatDate(dateStr) {
      return formatDate(dateStr)
    }
  }
}
</script>

<style scoped>
.page-container { display: flex; min-height: 100vh; background: #f5f5f5; }
.main-content { flex: 1; margin-left: 260px; padding: 30px; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.page-header h1 { font-size: 24px; color: #2c3e50; }
.header-actions { display: flex; gap: 10px; }
.add-btn { padding: 10px 20px; background: #e74c3c; color: white; border: none; border-radius: 5px; cursor: pointer; }
.add-btn:hover { background: #c0392b; }
.refresh-btn { padding: 10px 20px; background: #27ae60; color: white; border: none; border-radius: 5px; cursor: pointer; }
.refresh-btn:hover { background: #229954; }
.filter-bar { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
.search-input { padding: 10px 15px; border: 1px solid #ddd; border-radius: 5px; width: 200px; max-width: 25%; }
.status-select { padding: 10px 15px; border: 1px solid #ddd; border-radius: 5px; min-width: 120px; background: white; }
.search-btn { padding: 10px 20px; background: #3498db; color: white; border: none; border-radius: 5px; cursor: pointer; }
.reset-btn { padding: 10px 20px; background: #95a5a6; color: white; border: none; border-radius: 5px; cursor: pointer; }
.content-body { background: white; padding: 20px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
.loading, .error, .empty { text-align: center; padding: 50px; color: #666; }
.error { color: #e74c3c; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th { background: #f8f9fa; padding: 15px; text-align: left; font-weight: 600; border-bottom: 2px solid #eee; }
.data-table td { padding: 15px; border-bottom: 1px solid #eee; vertical-align: top; }
.data-table tr:hover { background: #f8f9fa; }
.name-cell { font-weight: 600; color: #2c3e50; }
.status-tag { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 12px; }
.status-tag.active { background: #e3f2fd; color: #1976d2; }
.status-tag.inactive { background: #ffebee; color: #c62828; }
.action-cell { display: flex; gap: 5px; }
.edit-btn { padding: 6px 12px; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; background: #f39c12; color: white; }
.delete-btn { padding: 6px 12px; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; background: #e74c3c; color: white; }
.pagination { display: flex; justify-content: center; align-items: center; gap: 15px; margin-top: 20px; }
.pagination button { padding: 8px 15px; background: white; border: 1px solid #ddd; border-radius: 5px; cursor: pointer; }
.pagination button:disabled { opacity: 0.5; cursor: not-allowed; }
.page-size-select { padding: 8px 10px; border: 1px solid #ddd; border-radius: 5px; background: white; cursor: pointer; }
.total { margin-top: 10px; text-align: center; color: #666; }

.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background: white; border-radius: 10px; max-height: 90vh; overflow-y: auto; }
.edit-modal { width: 600px; max-width: 95%; }
.delete-modal { width: 400px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid #eee; }
.modal-header h2 { font-size: 18px; color: #2c3e50; }
.close-btn { background: none; border: none; font-size: 24px; cursor: pointer; color: #666; }
.modal-body { padding: 20px; }
.modal-footer { padding: 20px; border-top: 1px solid #eee; display: flex; justify-content: flex-end; gap: 10px; }
.cancel-btn { padding: 10px 20px; background: #95a5a6; color: white; border: none; border-radius: 5px; cursor: pointer; }
.save-btn { padding: 10px 20px; background: #27ae60; color: white; border: none; border-radius: 5px; cursor: pointer; }
.save-btn:disabled { opacity: 0.5; }
.confirm-delete-btn { padding: 10px 20px; background: #e74c3c; color: white; border: none; border-radius: 5px; cursor: pointer; }
.confirm-delete-btn:disabled { opacity: 0.5; }

.edit-body { max-height: 60vh; overflow-y: auto; }
.form-row { margin-bottom: 15px; }
.form-row label { display: block; margin-bottom: 5px; font-weight: 500; color: #333; }
.required { color: #e74c3c; }
.form-input { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; }
.form-textarea { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; min-height: 80px; resize: vertical; }
.form-textarea.large { min-height: 300px; }
.form-select { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; background: white; }
</style>